import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Table, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { api } from '../../../shared/api';
import NumberInput from '../../../components/form/NumberInput';
import { getContrastColor, COLOR_SUCCESS } from '../../../shared/colorUtils';
import { useMutation } from '@tanstack/react-query';

const RentalReturnModal = ({ show, onHide, rental, onReturnComplete }) => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [itemsData, setItemsData] = useState([]);
  const [generalNote, setGeneralNote] = useState('');
  const [returnDate, setReturnDate] = useState(new Date().toISOString().slice(0, 16));

  const returnMutation = useMutation({
    mutationFn: () => api.post(`rentals/api/rentals/${rental.id}/return/`, {
      items: itemsData.map(item => ({
        rental_item_id: item.rental_item_id,
        quantity_returned: item.quantity_returned,
        quantity_damaged: item.quantity_damaged,
        quantity_lost: item.quantity_lost,
        quantity_stolen: item.quantity_stolen,
        return_note: item.return_note
      })),
      return_date: returnDate,
      general_note: generalNote
    }),
    onSuccess: (response) => {
      if (response.status === 'success' || response.status === 'partial_success') {
        setSuccess(response.message || t('rentals.return.success'));
        setTimeout(() => {
          if (onReturnComplete) onReturnComplete(response);
          onHide();
        }, 1500);
      } else {
        setError(response.error || t('rentals.return.error'));
      }
    },
    onError: (err) => setError(err.response?.data?.error || t('rentals.return.error')),
  });
  const loading = returnMutation.isPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateItems()) return;
    setError(null);
    setSuccess(null);
    returnMutation.mutate();
  };

  useEffect(() => {
    if (show && rental) {
      // Initialize items data from rental items
      const initialItemsData = rental.rental_items?.map(item => ({
        rental_item_id: item.id,
        item_name: item.stock_item_product_name || 'Unknown Item',
        quantity: item.quantity,
        quantity_returned: item.quantity_returned || 0,
        quantity_damaged: item.quantity_damaged || 0,
        quantity_lost: item.quantity_lost || 0,
        quantity_stolen: item.quantity_stolen || 0,
        return_note: item.return_note || '',
        is_fully_returned: item.is_fully_returned || false,
        quantity_pending: item.quantity_pending || item.quantity
      })) || [];

      setItemsData(initialItemsData);
      setError(null);
      setSuccess(null);
    }
  }, [show, rental]);

  const handleQuantityChange = (index, field, value) => {
    const newItemsData = [...itemsData];
    const numValue = parseInt(value) || 0;

    // Ensure value is not negative
    if (numValue < 0) return;

    newItemsData[index][field] = numValue;

    // Calculate pending quantity
    const totalAccounted =
      newItemsData[index].quantity_returned +
      newItemsData[index].quantity_damaged +
      newItemsData[index].quantity_lost +
      newItemsData[index].quantity_stolen;

    newItemsData[index].quantity_pending = Math.max(0, newItemsData[index].quantity - totalAccounted);
    newItemsData[index].is_fully_returned = totalAccounted >= newItemsData[index].quantity;

    setItemsData(newItemsData);
  };

  const handleNoteChange = (index, value) => {
    const newItemsData = [...itemsData];
    newItemsData[index].return_note = value;
    setItemsData(newItemsData);
  };

  const validateItems = () => {
    for (const item of itemsData) {
      const totalAccounted =
        item.quantity_returned +
        item.quantity_damaged +
        item.quantity_lost +
        item.quantity_stolen;

      if (totalAccounted > item.quantity) {
        setError(t('rentals.return.validation.exceeds') + ` - ${item.item_name}`);
        return false;
      }
    }

    // Check if at least some items have been processed
    const hasAnyProcessed = itemsData.some(item =>
      item.quantity_returned > 0 ||
      item.quantity_damaged > 0 ||
      item.quantity_lost > 0 ||
      item.quantity_stolen > 0
    );

    if (!hasAnyProcessed) {
      setError(t('rentals.return.validation.required'));
      return false;
    }

    return true;
  };


  const getTotalReturned = () => {
    return itemsData.reduce((sum, item) => sum + item.quantity_returned, 0);
  };

  const getTotalDamaged = () => {
    return itemsData.reduce((sum, item) => sum + item.quantity_damaged, 0);
  };

  const getTotalLost = () => {
    return itemsData.reduce((sum, item) => sum + item.quantity_lost, 0);
  };

  const getTotalStolen = () => {
    return itemsData.reduce((sum, item) => sum + item.quantity_stolen, 0);
  };

  const getTotalPending = () => {
    return itemsData.reduce((sum, item) => sum + item.quantity_pending, 0);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" fullscreen="sm-down">
      <Modal.Header closeButton>
        <Modal.Title>{t('rentals.return.title')} - {rental?.id}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
            {success}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>{t('rentals.return.returnDate')}</Form.Label>
            <Form.Control
              type="datetime-local"
              value={returnDate}
              onChange={(e) => setReturnDate(e.target.value)}
              required
            />
          </Form.Group>

          <div className="table-responsive mb-3">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>{t('rentals.return.item')}</th>
                  <th className="text-center">{t('rentals.return.quantity')}</th>
                  <th className="text-center">{t('rentals.return.quantityReturned')}</th>
                  <th className="text-center">{t('rentals.return.quantityDamaged')}</th>
                  <th className="text-center">{t('rentals.return.quantityLost')}</th>
                  <th className="text-center">{t('rentals.return.quantityStolen')}</th>
                  <th className="text-center">{t('rentals.return.quantityPending')}</th>
                  <th>{t('rentals.return.returnNote')}</th>
                </tr>
              </thead>
              <tbody>
                {itemsData.map((item, index) => (
                  <tr key={item.rental_item_id} className={item.is_fully_returned ? 'table-success' : ''}>
                    <td>
                      <strong>{item.item_name}</strong>
                      {item.is_fully_returned && (
                        <span className="badge ms-2" style={{ backgroundColor: COLOR_SUCCESS, color: getContrastColor(COLOR_SUCCESS) }}>{t('rentals.return.fullyReturned')}</span>
                      )}
                    </td>
                    <td className="text-center">
                      <strong>{item.quantity}</strong>
                    </td>
                    <td>
                      <NumberInput
                        min={0}
                        max={item.quantity}
                        step="any"
                        value={item.quantity_returned}
                        onChange={(value) => handleQuantityChange(index, 'quantity_returned', value)}
                        disabled={loading}
                        size="sm"
                      />
                    </td>
                    <td>
                      <NumberInput
                        min={0}
                        max={item.quantity}
                        step="any"
                        value={item.quantity_damaged}
                        onChange={(value) => handleQuantityChange(index, 'quantity_damaged', value)}
                        disabled={loading}
                        size="sm"
                      />
                    </td>
                    <td>
                      <NumberInput
                        min={0}
                        max={item.quantity}
                        step="any"
                        value={item.quantity_lost}
                        onChange={(value) => handleQuantityChange(index, 'quantity_lost', value)}
                        disabled={loading}
                        size="sm"
                      />
                    </td>
                    <td>
                      <NumberInput
                        min={0}
                        max={item.quantity}
                        step="any"
                        value={item.quantity_stolen}
                        onChange={(value) => handleQuantityChange(index, 'quantity_stolen', value)}
                        disabled={loading}
                        size="sm"
                      />
                    </td>
                    <td className="text-center">
                      <strong className={item.quantity_pending > 0 ? 'text-warning' : 'text-success'}>
                        {item.quantity_pending}
                      </strong>
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        rows={1}
                        value={item.return_note}
                        onChange={(e) => handleNoteChange(index, e.target.value)}
                        disabled={loading}
                        placeholder={t('rentals.return.returnNote')}
                        size="sm"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="table-secondary">
                  <td><strong>Total</strong></td>
                  <td className="text-center"><strong>{itemsData.reduce((sum, item) => sum + item.quantity, 0)}</strong></td>
                  <td className="text-center"><strong>{getTotalReturned()}</strong></td>
                  <td className="text-center"><strong>{getTotalDamaged()}</strong></td>
                  <td className="text-center"><strong>{getTotalLost()}</strong></td>
                  <td className="text-center"><strong>{getTotalStolen()}</strong></td>
                  <td className="text-center"><strong className={getTotalPending() > 0 ? 'text-warning' : 'text-success'}>{getTotalPending()}</strong></td>
                  <td></td>
                </tr>
              </tfoot>
            </Table>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>{t('rentals.return.generalNote')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={generalNote}
              onChange={(e) => setGeneralNote(e.target.value)}
              disabled={loading}
              placeholder={t('rentals.return.generalNote')}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          {t('rentals.return.cancel')}
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading && <Spinner as="span" size="sm" animation="border" className="me-2" />}
          {t('rentals.return.processReturn')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RentalReturnModal;
